import { addCommonDiagnosticProperties, type DiagnosticOverrides, } from "./utils/addCommonDiagnosticProperties";
import type { ApplicationInsights, IExtendedTelemetryItem } from "@microsoft/1ds-analytics-js";
import type { CustomDataMap, DataPointEventType, CoreAnalyticsOptions } from "owa-analytics-types";
import type { AccountKey } from "owa-client-types";
import type { AppInisightsTenants, CachedProperties } from "./types/OneDsWrapperTypes";
import { INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT } from "owa-analytics-types";
import { getApp } from "owa-config/lib/bootstrapOptions";
import { scrubForPii } from "owa-config/lib/scrubForPii";
import { getMsaDeviceTicket } from "owa-config/lib/msaDeviceTicket";
import { trace } from "owa-trace";
import { getCollectorUrl, getCollectorUrlForMailbox } from "owa-analytics-utils";
import { hashString } from "owa-analytics-shared/lib/utils/hashString";
import { convertTo1DSTypes } from "./utils/convertTo1DSTypes";
import { UNIFIED_SCHEMA_SENTINEL, PRIVACY_CONCERN_SENTINEL, } from "./plugins/SchemaEnforcementPlugin";
import { trackEventFrequency } from "./utils/trackEventFrequency";
import { getCachedSettings, resolveAnalyticsCacheKey } from "./settings/getCachedSettings";
import { getAnalyticsFlightsAndAppSettings } from "./settings/getAnalyticsFlightsAndAppSettings";
import { sendEventTo1DS } from "./utils/sendEventTo1DS";
import { initializeAppInsights } from "./initializeAppInsights";
import { isRunningOnWorker } from "owa-config/lib/isRunningOnWorker";
import { logDatapoint } from "./loggingFunctions/logDatapoint";
import { AriaCoreDatapoint } from "./datapoints/AriaCoreDatapoint";
import { setOneDsEventBaseType } from "./utils/setOneDsEventBaseType";

let insights: AppInisightsTenants = {};
let tenantInitializationPromise: {
    [tenant: string]: Promise<void> | null;
} = {};
let cachedProperties: CachedProperties = {};

let mismatchedCollectorUrlLogged = false;

/**
 * Using this for an investigation for event being duplicated
 */
const eventDuplicationCheck: string[] = [];

/**
 * Logs events that are missing MailboxInfo (accountKey) to help investigate missing events.
 */
const missingMailboxInfoLoggedEvents: Set<string> = new Set();

/**
 * List of events that are reported as having different endpoints URLs but that we validated are okay.
 */
const expectedMismatchTablesOrEvents = new Set<string>([
    "Ms.Fast.Px.Render", // Special people table used to always be logged in EU only
    "Ms.Fast.Px.SessionStart", // Special people table used to always be logged in EU only
    "Ms.Fast.Px.SessionEnd", // Special people table used to always be logged in EU only
]);

export async function logOneDSDatapoint({ tenantToken, item, analyticsOptions, accountKey, overrideEndpointUrl, skipCommonProperties = false, useUnifiedSchema = false, customDiagnosticsOptions = {}, }: {
    tenantToken: string;
    item: IExtendedTelemetryItem;
    analyticsOptions?: CoreAnalyticsOptions;
    accountKey: AccountKey | "globalAccount" | undefined;
    overrideEndpointUrl?: string;
    skipCommonProperties?: boolean;
    useUnifiedSchema?: boolean;
    updateAppSettings?: boolean;
    customDiagnosticsOptions?: CustomDataMap;
}): Promise<void> {
    // While rolling out the new collector endpoints, we want to log mismatches
    const forest = analyticsOptions?.startupInfo.forest;
    const newEndpointUrl = overrideEndpointUrl ?? (await getCollectorUrlForMailbox(accountKey, forest));
    const endpointUrl = getEndpointUrlWithOverrides(analyticsOptions, overrideEndpointUrl);
    const eventName = item.data?.EventName as string | undefined;
    setOneDsEventBaseType(item);
    const table = item.name;
    const effectiveAccountKey = accountKey ?? "globalAccount";
    const cachedSettings = getCachedSettings(effectiveAccountKey);
    const logicalRing = cachedSettings?.sessionInfo?.logicalRing;
    
    // Only check for mistmatches if the old one is defined. The new one should always be defined.
    if (endpointUrl &&
        newEndpointUrl !== endpointUrl &&
        !mismatchedCollectorUrlLogged &&
        !expectedMismatchTablesOrEvents.has(eventName ?? "") &&
        !expectedMismatchTablesOrEvents.has(table)) {
        mismatchedCollectorUrlLogged = true;
        trace.warn(`[OneDS] Collector URL mismatch for tenantToken: ${tenantToken}, table: ${item.name}, eventName: ${eventName}, oldEndpointUrl: ${endpointUrl}, newEndpointUrl: ${newEndpointUrl}`, "analytics");
        logDatapoint(new AriaCoreDatapoint("CollectorUrlMismatch", INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT, {
            newEndpointUrl,
            endpointUrl,
            tenantToken,
            hasMailboxInfo: !!accountKey,
            isOverrideEndpointUrl: !!overrideEndpointUrl,
            eventName,
            table,
        }));
    }
    
    // Log datapoint that are missing a MailboxInfo key (accountKey)
    if (!accountKey &&
        logicalRing === "Dogfood" && // Limit to Dogfood to avoid overlogin
        eventName &&
        !missingMailboxInfoLoggedEvents.has(eventName)) {
        trace.warn(`[OneDS] No collector URL found for tenantToken: ${tenantToken}, table: ${item.name}, eventName: ${eventName}, accountKey: ${accountKey}`, "analytics");
        missingMailboxInfoLoggedEvents.add(eventName);
        
        logDatapoint(new AriaCoreDatapoint("MissingMailboxInfo", INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT, {
            eventName,
            newEndpointUrl,
            endpointUrl,
            tenantToken,
            isOverrideEndpointUrl: !!overrideEndpointUrl,
            table,
        }, {
            sessionSampleRate: 0.1,
        }));
    }
    
    const diagnosticOverrides: DiagnosticOverrides = {
        IsRunningInAWorker: isRunningOnWorker(),
    };
    
    const cacheKey = resolveAnalyticsCacheKey(accountKey, "logOneDSDatapoint");
    const analyticsInstanceKey = createAnalyticsInstanceKey(tenantToken, cacheKey, newEndpointUrl ?? endpointUrl ?? "default");
    
    // If the tenant is not initialized, initialize it
    if (!insights[analyticsInstanceKey] && !tenantInitializationPromise[analyticsInstanceKey]) {
        trace.info(`Creating tenant key for tenantToken: ${tenantToken}, accountKey: ${accountKey}, endpointUrl: ${newEndpointUrl ?? endpointUrl}, hash: ${analyticsInstanceKey}`, "analytics");
        
        // We do not await the promise here so we can block other incoming events
        tenantInitializationPromise[analyticsInstanceKey] = initializeAppInsights(insights, cachedProperties, newEndpointUrl ?? endpointUrl, tenantToken, analyticsInstanceKey, analyticsOptions, skipCommonProperties, useUnifiedSchema, diagnosticOverrides, effectiveAccountKey);
    }
    
    // Wait for the tenant to be initialized
    await tenantInitializationPromise[analyticsInstanceKey];
    
    // Set the MSA ticket if it has changed. We don't have it at boot so it will always get added later in the session.
    const msaDeviceTicket = getMsaDeviceTicket();
    const msaDeviceTicketChanged = cachedProperties[analyticsInstanceKey]?.msaDeviceTicket !== msaDeviceTicket;
    if (msaDeviceTicket && msaDeviceTicketChanged) {
        // This ticket is only available in Native
        insights[analyticsInstanceKey].getPostChannel()?.setMsaAuthTicket(msaDeviceTicket);
        cachedProperties[analyticsInstanceKey].msaDeviceTicket = msaDeviceTicket;
        diagnosticOverrides.MsaDeviceTicketChanged = true;
    }
    
    // Convert the event to a format that 1DS can understand
    if (useUnifiedSchema && item.data) {
        item.data["UserInfo.Id"] = item.data.UserId?.toString();
        item.data["UserInfo.IdType"] = item.data.UserIdType?.toString();
        delete item.data.UserId;
        delete item.data.UserIdType;
        
        item.data = convertTo1DSTypes(item.data);
        
        // Mark the event so SchemaEnforcementPlugin skips column validation.
        // Unified-schema events have dynamic per-event columns that cannot be
        // enumerated in a static allowlist.  The sentinel is stripped by the
        // plugin before the event reaches the POST channel.
        // Only stamp when the plugin is active (flight on) to avoid leaking
        // the sentinel as a real Aria column.
        if (item.data &&
            getAnalyticsFlightsAndAppSettings(effectiveAccountKey)?.enableSchemaEnforcement) {
            item.data[UNIFIED_SCHEMA_SENTINEL] = 1;
        }
    }
    
    // PrivacyConcern events have their own column schema outside OWA's static
    // allowlist.  Mark them with the sentinel so SchemaEnforcementPlugin
    // skips column validation while the table guard still applies.
    if (item.name === "PrivacyConcern" &&
        item.data &&
        getAnalyticsFlightsAndAppSettings()?.enableSchemaEnforcement) {
        item.data[PRIVACY_CONCERN_SENTINEL] = 1;
    }
    
    // Add app and host telemetry to the event
    if (!useUnifiedSchema && item.data) {
        if (!item.data.App) {
            item.data.App = getApp();
            trace.warn(` Event ${item.name} should contain a App field.`);
        }
        
        const hostTelemetry = scrubForPii(cachedSettings?.opxSessionInfo?.hostTelemetry);
        if (hostTelemetry) {
            item.data.HostTelemetry = hostTelemetry;
        }
    }
    
    if (!self.navigator.onLine) {
        diagnosticOverrides.Offline = true;
    }
    
    // Investigation for event duplication
    if (item.data?.EventName && item.data?.SequenceNumber) {
        const eventId = `${item.data?.EventName}-${item.data?.SequenceNumber}`;
        if (eventDuplicationCheck.includes(eventId)) {
            diagnosticOverrides.EventDuplicated = true;
            
            // Keep the array small to avoid affecting memory and performance.
            // Fix (PR 228162): was .unshift() (no-op with zero args); changed
            // to .shift() to actually remove the oldest entry.
            if (eventDuplicationCheck.length > 25) {
                eventDuplicationCheck.shift();
            }
        }
        else {
            eventDuplicationCheck.push(eventId);
        }
    }
    
    // Add diagnostics overrides and set endpoint url
    addCommonDiagnosticProperties(endpointUrl, item, diagnosticOverrides, customDiagnosticsOptions);
    
    // schema-e2e: column-guard injection point — do not remove
    //if (item.data) {
    //    item.data['junk_test_column'] = 'injected_value';
    //} // TEST - comment out before commit
    
    // Send the event to 1DS
    sendEventTo1DS(insights[analyticsInstanceKey], item);
    
    // Track the event frequency
    trackEventFrequency(item);
}

function createAnalyticsInstanceKey(tenantToken: string, cacheKey: string, endpointUrl: string): string {
    const rawKey = `${tenantToken}|${cacheKey}|${endpointUrl}`;
    return hashString(rawKey);
}

function getEndpointUrlWithOverrides(analyticsOptions?: CoreAnalyticsOptions, overrideEndpointUrl?: string) {
    if (overrideEndpointUrl) {
        return overrideEndpointUrl;
    }
    
    if (analyticsOptions?.endpointUrlOverride === undefined) {
        // Override is not passed in analyticsOptions, hence use the fallback collector url.
        return getCollectorUrl(analyticsOptions?.startupInfo.forest);
    }
    
    return analyticsOptions.endpointUrlOverride === null
        ? undefined // Converting null to undefined, to use the default 1DS collector url.
        : analyticsOptions.endpointUrlOverride;
}

export function oneDSFlush(): void {
    for (const analytics of Object.values(insights) as ApplicationInsights[]) {
        if (analytics && analytics.isInitialized()) {
            try {
                analytics.getPostChannel().flush();
            }
            catch { }
        }
    }
}

export function addMsaAuthTicket(ticket: string): void {
    for (const analytics of Object.values(insights)) {
        if (analytics) {
            try {
                analytics.getPostChannel().setMsaAuthTicket(ticket);
            }
            catch { }
        }
    }
}

export function createEvent(eventName: DataPointEventType, props?: CustomDataMap, time?: number): IExtendedTelemetryItem {
    return {
        name: eventName,
        data: props ? JSON.parse(JSON.stringify(props)) : {},
        time: (time ? new Date(time) : new Date()).toISOString(),
    };
}

export function TEST_ONLY_reset_globals(): void {
    insights = {};
    tenantInitializationPromise = {};
    cachedProperties = {};
}
